import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";

const CountrySelector = ({
  selectedCountry,
  onCountryChange,
  t,
  apiLanguageCode,
  className
}) => {
  const [countries, setCountries] = useState([]);
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);
  const countrySelectRef = useRef(null);

  const priorityCountryCodes = ['CH', 'DE', 'FR', 'AT', 'IT'];

  useEffect(() => {
    const fetchCountries = async () => {
      setIsLoadingCountries(true);
      try {
        const response = await fetch('https://restcountries.com/v3.1/all?fields=name,cca2,translations');
        if (response.ok) {
          const data = await response.json();

          const mappedCountries = data.map(country => {
            let localizedName;

            if (country.translations[apiLanguageCode]) {
              localizedName = country.translations[apiLanguageCode].common;
            }
            else if (country.translations[convertLanguageCode(apiLanguageCode, 'two-letter')]) {
              localizedName = country.translations[convertLanguageCode(apiLanguageCode, 'two-letter')].common;
            }
            else {
              localizedName = country.name.common;
            }

            return {
              name: localizedName,
              code: country.cca2,
              isPriority: priorityCountryCodes.includes(country.cca2)
            };
          });

          const sortedCountries = mappedCountries.sort((a, b) => {
            if (a.isPriority && !b.isPriority) return -1;
            if (!a.isPriority && b.isPriority) return 1;

            if (a.isPriority && b.isPriority) {
              return priorityCountryCodes.indexOf(a.code) - priorityCountryCodes.indexOf(b.code);
            }

            return a.name.localeCompare(b.name);
          });

          setCountries(sortedCountries);
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
      } finally {
        setIsLoadingCountries(false);
      }
    };

    fetchCountries();
  }, [apiLanguageCode]);

  const handleCountryChange = (e) => {
    const selectedCountryCode = e.target.value;
    const selectedCountry = countries.find(c => c.code === selectedCountryCode);

    onCountryChange({
      code: selectedCountryCode,
      name: selectedCountry ? selectedCountry.name : ''
    });
  };



  return (
    <select
      ref={countrySelectRef}
      className={className}
      onChange={handleCountryChange}
      value={selectedCountry || ''}
      disabled={isLoadingCountries}
    >
      <option disabled value="">
        {isLoadingCountries
          ? t("Order.LoadingCountries")
          : t("Order.SelectCountry")}
      </option>

      {countries.map(country => (
        <option
          key={country.code}
          value={country.code}
          style={country.isPriority ? { fontWeight: 'bold' } : {}}
        >
          {country.name} ({country.code})
        </option>
      ))}
    </select>
  );
};

CountrySelector.propTypes = {
  selectedCountry: PropTypes.string,
  onCountryChange: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  apiLanguageCode: PropTypes.string.isRequired,
  className: PropTypes.string
};
export const convertLanguageCode = (code, toFormat = 'two-letter') => {
  const languageCodeMap = {
    'DE': 'deu',
    'FR': 'fra',
    'IT': 'ita',
    'EN': 'eng',

    'deu': 'DE',
    'fra': 'FR',
    'ita': 'IT',
    'eng': 'EN',
  };

  const normalizedCode = code.toUpperCase();

  if (toFormat === 'two-letter') {
    if (normalizedCode.length === 2 && Object.keys(languageCodeMap).includes(normalizedCode)) {
      return normalizedCode;
    }

    return languageCodeMap[code.toLowerCase()] || 'EN';
  } else {
    return languageCodeMap[normalizedCode] || 'eng';
  }
};
export default CountrySelector;